import { returnFormattedDate } from "@/lib/globalFormatterHelper";
import { Slider } from "@mui/material";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import { IBaseFilterBrokerRank } from "../BrokerRank/BrokerRankIndex";
import { SankeyDataFilter } from "../FlowAnalysis/IndexFlowAnalysis";
import { IClientSummaryFilters } from "./IndexClientSummary";
import { ITransactionCostFilters } from "../TransactionCost/TransactionCostIndex";
import { ICounterpartyReportFilters } from "../CounterpartyReport/CounterpartyReportIndex";

function dateToMilliseconds(date: Date) {
  return date.getTime();
}

function resetSevenDayFilter(
  dataFilters: IClientSummaryFilters,
  setDataFilters: any,
) {
  setDataFilters((prev: number[]) => ({
    ...prev,
    activeDay: undefined,
  }));
}

const DateRangeAndSlider = ({
  dataFilters,
  setDataFilters,
  isSecondary,
}: {
  dataFilters:
    | IBaseFilterBrokerRank
    | SankeyDataFilter
    | IClientSummaryFilters
    | ITransactionCostFilters
    | ICounterpartyReportFilters;
  setDataFilters: Function;
  isSecondary: boolean;
}) => {
  const { theme } = useTheme();

  const [range, setRange] = useState([
    dateToMilliseconds(new Date("2025-01-01T00:00")),
    dateToMilliseconds(new Date("2026-01-01T00:00")),
  ]);

  const MinDate = new Date("2025-01-01T00:00");
  const MaxDate = new Date();

  const [startDate, setStartDate] = useState(MinDate);
  const [endDate, setEndDate] = useState(MaxDate);

  const handelSliderChange = (
    unusedEvent: Event,
    newValue: number | number[],
  ) => {
    if (isSecondary) {
      if ("activeDay" in dataFilters) {
        resetSevenDayFilter(dataFilters, setDataFilters);
      }
    }
    if (!Array.isArray(newValue)) {
      return;
    }

    // order the values
    const [a, b] = newValue;
    const newMinDate = Math.min(a, b);
    const newMaxDate = Math.max(a, b);
    // Set the dates on the Slider
    setRange([newMinDate, newMaxDate]);
    // Need to update BOTH date pickers.

    setStartDate(new Date(newMinDate));
    setEndDate(new Date(newMaxDate));
  };

  // When the range updates - Trigger updating the filters
  useEffect(() => {
    const handler = setTimeout(() => {
      setDataFilters((prev: number[]) => ({ ...prev, range }));
    }, 500); // debounce delay in ms

    return () => {
      clearTimeout(handler); // clear timeout if range changes before delay
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  function updateSliderFromDatePicker(datePassed: Date, level: "Min" | "Max") {
    const millisecondsDate = datePassed.getTime();
    if (level == "Min") {
      setRange([millisecondsDate, range[1]]);
    } else {
      setRange([range[0], millisecondsDate]);
    }
  }

  function updateSliderFromDatePickerToBothBeTheSameValue(datePassed: Date) {
    const millisecondsDate = datePassed.getTime();
    setRange([millisecondsDate, millisecondsDate]);
  }

  function updateDatePickers(datePassed: Date, level: "Min" | "Max") {
    if (level == "Min") {
      // Check if the new min date is > max date - If so, update the Max date.
      if (datePassed > endDate) {
        updateSliderFromDatePickerToBothBeTheSameValue(datePassed);
        setEndDate(new Date(datePassed));
        setStartDate(datePassed);
      } else {
        setStartDate(datePassed);
        updateSliderFromDatePicker(datePassed, "Min");
      }
    } else if (level == "Max") {
      // Check if the new Max date is < min date - If so, update the Min date.
      if (datePassed < startDate) {
        updateSliderFromDatePickerToBothBeTheSameValue(datePassed);
        setStartDate(new Date(datePassed));
        setEndDate(datePassed);
      } else {
        setEndDate(datePassed);
        updateSliderFromDatePicker(datePassed, "Max");
      }
    }
  }

  function updateSliderColour() {
    if (theme == "dark") {
      return "#8D99AE";
    } else {
      return "#8D99AE";
    }
  }

  const [setsliderColour, setSliderColour] = useState(updateSliderColour());

  useEffect(() => {
    setSliderColour(updateSliderColour());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  return (
    <div className="flex h-fit flex-col">
      <div className="flex">
        <div className="text-md">Date Range:</div>
      </div>

      <div className="flex justify-between px-2 pt-2">
        <DatePicker
          dateFormat="d MMM, yyyy"
          selected={startDate}
          onChange={(date) => {
            if (!date) {
              return;
            }
            if (date < MinDate) {
              toast.error(
                `Selected date is before minimum range of ${returnFormattedDate(MinDate)}`,
              );
              return;
            }
            updateDatePickers(date, "Min");
          }}
          id="overwriteDatePickerAnalytics"
          className={`$ w-[90px] border text-center text-sm dark:border-gray-500`}
        />
        <DatePicker
          dateFormat="d MMM, yyyy"
          selected={endDate}
          onChange={(date) => {
            if (!date) {
              return;
            }
            if (date > MaxDate) {
              toast.error(
                `Selected date is after maximis range of ${returnFormattedDate(MaxDate)}`,
              );
              return;
            }
            updateDatePickers(date, "Max");
          }}
          id="overwriteDatePickerAnalytics"
          className={`$ w-[90px] border text-center text-sm dark:border-gray-500`}
        />
      </div>
      <div className="px-2 pt-2">
        <Slider
          getAriaLabel={() => "Date Range"}
          value={range}
          onChange={handelSliderChange}
          valueLabelDisplay="auto"
          valueLabelFormat={(val) => returnFormattedDate(val)}
          //   getAriaValueText={valueText}
          min={dateToMilliseconds(MinDate)}
          max={dateToMilliseconds(MaxDate)}
          sx={{
            color: setsliderColour, // Tailwind's yellow-400
            "& .MuiSlider-thumb": {
              // borderRadius: "4px",
              width: "15px",
              height: "15px",
            },
            "& .MuiSlider-track": {
              border: "none",
            },
            "& .MuiSlider-rail": {
              // opacity: 0.5,
              backgroundColor: "#515d70", // Tailwind gray-300
            },
          }}
        />
      </div>
    </div>
  );
};

export default DateRangeAndSlider;
